import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { workflowApi } from "../../Apis/Api";
import useDebounce from "../../hook/useDebounce";
import Breadcrumb from "../../components/Breadcrumb";
import TableDataNoAvailable from "../../components/ui/TableDataNoAvailable";
import { CHECK_IN_PHOTO } from "../../utils/Contants";
import ErrorAlert from "../../components/ui/ErrorAlert";
import CustomSelect from "../../components/common/CustomSelect";
import PerPageDropdown from "../../components/ui/PerPageDropdown";
import PaginationControls from "../../components/ui/PaginationControls";
import { Button } from "react-bootstrap"; // Added for Button component
import { Printer } from "lucide-react";
import TicketComponent from "../../components/workflow/TicketComponent";
import ReceiptComponent from "../../components/workflow/ReceiptComponent";
import InfoModal from "../../components/ui/InfoeModal"; // 👈 NEW: Import InfoModal
import StepHistoryModal from "../../components/workflow/StepHistoryModal";

const WorkFlowInstanceHistory = () => {
  const [instances, setInstances] = useState([]);
  const [isContinuing, setIsContinuing] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [instanceToCancel, setInstanceToCancel] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [sortConfig, setSortConfig] = useState({
    field: "instance_id",
    direction: "desc",
  });

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(filters.search, 500);

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  useEffect(() => {
    fetchInstances();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
  ]);

  useEffect(() => {
    let interval;
    if (showImageModal && selectedImages.length > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex(
          (prevIndex) => (prevIndex + 1) % selectedImages.length
        );
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [showImageModal, selectedImages]);

  const fetchInstances = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        status: filters.status,
        sort_by: sortConfig.field,
        sort_order: sortConfig.direction,
      };

      const response = await workflowApi.getInstanceHistory(params);

      if (!response.success) {
        throw new Error(
          response.message || "Failed to fetch workflow instances"
        );
      }

      if (!Array.isArray(response.data)) {
        throw new Error("Invalid API response: instances data missing");
      }

      const formattedData = response.data.map((instance) => {
        const stepCount = instance.step_history?.length || 0;
        const isCompleted = instance.status === "completed";
        const isCancelled = instance.status === "cancelled";

        const vehicleDetailsStep = instance.step_history.find(
          (step) => step.step_label === "Add Vehicle Details(Scenario 1,2)"
        );
        const vehicleName = vehicleDetailsStep
          ? `${vehicleDetailsStep.submitted_data.vehicle_make || "Unknown"} ${
              vehicleDetailsStep.submitted_data.vehicle_model || "Model"
            }`
          : "Unknown Vehicle";

        const imageStep = instance.step_history.find(
          (step) => step.step_label === "Capture Vehicle Images(Scenario1,2)"
        );
        const vehicleImages = [];
        if (imageStep?.submitted_data) {
          const { front_image, back_image, left_image, right_image } =
            imageStep.submitted_data;
          if (front_image?.length > 0) {
            vehicleImages.push(
              ...front_image.map((img) => `${CHECK_IN_PHOTO}/${img}`)
            );
          }
          if (back_image?.length > 0) {
            vehicleImages.push(
              ...back_image.map((img) => `${CHECK_IN_PHOTO}/${img}`)
            );
          }
          if (left_image?.length > 0) {
            vehicleImages.push(
              ...left_image.map((img) => `${CHECK_IN_PHOTO}/${img}`)
            );
          }
          if (right_image?.length > 0) {
            vehicleImages.push(
              ...right_image.map((img) => `${CHECK_IN_PHOTO}/${img}`)
            );
          }
        }

        return {
          id: instance.instance_id,
          workflow_name: instance.workflow_name,
          vehicle_name: vehicleName,
          vehicle_images: vehicleImages,
          workflow_id: instance.workflow_id,
          current_step_label: isCompleted
            ? "Completed"
            : isCancelled
            ? `Cancelled at: ${instance.cancelled_at_step || "Unknown step"}`
            : instance.current_step_id || "-",
          current_step_code: isCompleted
            ? "COMPLETED"
            : isCancelled
            ? "CANCELLED"
            : instance.current_step_id || "-",
          step_count: stepCount,
          status:
            instance.status.charAt(0).toUpperCase() + instance.status.slice(1),
          status_label: instance.status,
          assigned_to: "Unassigned",
          created_at: new Date(
            instance.step_history[0]?.submitted_at || new Date()
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
          raw_created_at: new Date().toISOString(),
          step_history: instance.step_history || [],
          location_id: instance.location_id,
          location_name: instance.location_name,
          cancelled_at_step: instance.cancelled_at_step,
          is_completed: isCompleted,
          is_cancelled: isCancelled,
        };
      });

      setInstances(formattedData);
      setPagination({
        currentPage: response.pagination.page,
        perPage: response.pagination.per_page,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      });
    } catch (err) {
      console.error("Fetch Error:", err);
      setError(err.message || "Failed to fetch workflow instances.");
      setInstances([]);
      toast.error(err.message || "Failed to fetch workflow instances.");
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (selected) => {
    const newStatus = selected?.value || "";
    setFilters((prev) => ({ ...prev, status: newStatus }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleViewDetails = (instance) => {
    setSelectedInstance(instance);
    setShowModal(true);
  };

  const handleContinue = async (instance) => {
    if (
      isContinuing[instance.id] ||
      instance.is_completed ||
      instance.is_cancelled
    )
      return;

    setIsContinuing((prev) => ({ ...prev, [instance.id]: true }));

    try {
      navigate(`/workflows-running`, {
        state: {
          instanceId: instance.id,
          workflowId: instance.workflow_id,
          workflowName: instance.workflow_name,
          stepCount: instance.step_count,
          locationId: instance.location_id,
          locationName: instance.location_name,
        },
      });
    } catch (error) {
      console.error("Continue Error:", error);
      toast.error(error.message || "Failed to continue workflow.");
    } finally {
      setIsContinuing((prev) => ({ ...prev, [instance.id]: false }));
    }
  };

  const handleCancel = (instance) => {
    setInstanceToCancel(instance);
    setShowCancelConfirmModal(true);
  };

  const confirmCancel = async () => {
    if (!instanceToCancel?.id) {
      toast.error("Cannot cancel workflow. Invalid instance data.");
      setShowCancelConfirmModal(false);
      return;
    }

    try {
      toast.success("Workflow cancellation would be handled here");
      fetchInstances();
    } catch (err) {
      console.error("Cancel Error:", err);
      toast.error(err.message || "Failed to cancel workflow instance.");
    } finally {
      setShowCancelConfirmModal(false);
      setInstanceToCancel(null);
    }
  };

  const handleCloseCancelModal = () => {
    setShowCancelConfirmModal(false);
    setInstanceToCancel(null);
  };

  const handleImageClick = (images) => {
    if (images.length > 0) {
      setSelectedImages(images);
      setCurrentImageIndex(0);
      setShowImageModal(true);
    }
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImages([]);
    setCurrentImageIndex(0);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(
      (prevIndex) =>
        (prevIndex - 1 + selectedImages.length) % selectedImages.length
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % selectedImages.length
    );
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      in_progress: "bg-primary-600 text-white border border-primary-main",
      completed: "bg-success-focus text-success-600 border border-success-main",
      pending: "bg-warning-focus text-warning-600 border border-warning-main",
      failed: "bg-danger-focus text-danger-600 border border-danger-main",
      cancelled: "bg-neutral-200 text-neutral-600 border border-neutral-400",
    };

    const normalizedStatus = status.toLowerCase();
    return (
      statusClasses[normalizedStatus] ||
      "bg-neutral-200 text-neutral-600 border border-neutral-400"
    );
  };

  const getVehicleImage = (vehicleImages) => {
    if (vehicleImages.length > 0) {
      return (
        <img
          src={vehicleImages[0]}
          alt="Vehicle"
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid #e2e8f0",
            cursor: "pointer",
          }}
          onClick={() => handleImageClick(vehicleImages)}
        />
      );
    }
    return <span style={{ fontSize: "40px" }}>🏍️</span>;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInstance(null);
  };
  const renderSubmittedData = (data, depth = 0, fieldName = "") => {
    if (!data || typeof data !== "object") {
      return <span>{data?.toString() || "-"}</span>;
    }

    // Handle ticket_summary case
    if (fieldName === "ticket_summary") {
      return <TicketComponent data={data} fieldName={fieldName} />;
    }

    // Handle receipt case
    if (fieldName === "reciept_image" || fieldName === "receipt") {
      return <ReceiptComponent data={data} fieldName={fieldName} />;
    }

    if (Array.isArray(data)) {
      return (
        <ul className="list-unstyled ms-3">
          {data.map((item, idx) => (
            <li key={idx}>{renderSubmittedData(item, depth + 1)}</li>
          ))}
        </ul>
      );
    }

    return (
      <ul className={`list-unstyled ${depth > 0 ? "ms-3" : ""}`}>
        {Object.entries(data).map(([key, value]) => {
          const isImageField =
            key.toLowerCase().includes("image") ||
            key.toLowerCase().includes("photo") ||
            key.toLowerCase().includes("receipt_image");

          const displayKey = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());

          let displayValue;
          if (value === null || value === undefined) {
            displayValue = "-";
          } else if (typeof value === "object") {
            displayValue = renderSubmittedData(value, depth + 1, key);
          } else {
            displayValue = value.toString();
          }

          return (
            <li key={key} className="mb-2 d-flex gap-2 align-items-start">
              <strong className="text-capitalize">{displayKey}:</strong>
              {isImageField && typeof value === "string" && value ? (
                <div className="image-container">
                  <img
                    src={`${CHECK_IN_PHOTO}/${value}`}
                    alt={key}
                    className="img-thumbnail"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                    }}
                    onClick={() =>
                      window.open(`${CHECK_IN_PHOTO}/${value}`, "_blank")
                    }
                  />
                </div>
              ) : isImageField && Array.isArray(value) && value.length > 0 ? (
                <div className="d-flex gap-2 flex-wrap">
                  {value.map((img, idx) => (
                    <img
                      key={idx}
                      src={`${CHECK_IN_PHOTO}/${img}`}
                      alt={`${key}-${idx}`}
                      className="img-thumbnail"
                      style={{
                        maxWidth: "150px",
                        maxHeight: "150px",
                      }}
                      onClick={() =>
                        window.open(`${CHECK_IN_PHOTO}/${img}`, "_blank")
                      }
                    />
                  ))}
                </div>
              ) : (
                <div>{displayValue}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchInstances} />;
  }

  return (
    <>
      <style>
        {`
          .workflow-card {
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            border: 1px solid #e5e7eb;
            transition: all 0.2s ease;
            cursor: pointer;
          }
          
          .workflow-card:hover {
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
            transform: translateY(-2px);
          }
          
          .workflow-card-header {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 12px;
          }
          
          .workflow-icon {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: #f8fafc;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            flex-shrink: 0;
            border: 2px solid #e2e8f0;
          }
          
          .workflow-content {
            flex: 1;
            min-width: 0;
          }
          
          .workflow-title {
            font-size: 18px;
            font-weight: 600;
            margin: 0 0 8px 0;
            line-height: 1.3;
          }
          
          .workflow-meta {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 8px;
            flex-wrap: wrap;
          }
          
          .workflow-meta-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            color: #6b7280;
          }
          
          .workflow-meta-item .icon {
            font-size: 16px;
          }
          
          .workflow-date {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            color: #9ca3af;
            margin-bottom: 16px;
          }
          
          .workflow-date .icon {
            font-size: 14px;
          }
          
          .status-badge-card {
            display: inline-flex;
            align-items: center;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-transform: lowercase;
            position: absolute;
            top: 16px;
            right: 16px;
          }
          
          .status-completed {
            background: #dcfce7;
            color: #166534;
          }
          
          .status-cancelled {
            background: #fee2e2;
            color: #dc2626;
          }
          
          .status-in_progress {
            background: #fef3c7;
            color: #d97706;
          }
          
          .status-pending {
            background: #fef3c7;
            color: #d97706;
          }
          
          .status-failed {
            background: #fee2e2;
            color: #dc2626;
          }
          
          .workflow-actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
            align-items: center;
            flex-wrap: wrap;
          }
          
          .action-btn-card {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            border: 1px solid #e5e7eb;
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .action-btn-card:hover {
            transform: scale(1.05);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          
          .action-btn-view {
            background: #3b82f6;
            border-color: #3b82f6;
            color: white;
          }
          
          .action-btn-view:hover {
            background: #2563eb;
            border-color: #2563eb;
          }
          
          .action-btn-cancel {
            background: #ef4444;
            border-color: #ef4444;
            color: white;
          }
          
          .action-btn-cancel:hover {
            background: #dc2626;
            border-color: #dc2626;
          }
          
          .workflow-card-body {
            position: relative;
            padding: 0;
          }
          
          .image-modal-content {
            max-width: 600px;
            margin: auto;
          }
          
          .image-modal-img {
            width: 100%;
            max-height: 80vh;
            object-fit: contain;
            border-radius: 8px;
          }

          .image-controls {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 16px;
            margin-top: 16px;
          }

          .image-control-btn {
            background: rgba(0, 0, 0, 0.5);
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            transition: background 0.15s;
          }

          .image-control-btn:hover {
            background: rgba(0, 0, 0, 0.7);
          }

          .image-index {
            color: #666;
            font-size: 14px;
          }
          
          .shimmer-wrapper {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .shimmer-card {
            height: 180px;
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite linear;
            border-radius: 12px;
          }

          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }

          /* Responsive Styles */
          @media (max-width: 991px) {
            .card-header {
              flex-direction: column;
              align-items: stretch;
            }

            .navbar-search {
              width: 100%;
              margin-bottom: 12px;
            }

            .PerPageDropdown {
              width: 100%;
              margin-bottom: 12px;
            }

            .workflow-card-header {
              flex-direction: column;
              align-items: center;
              text-align: center;
            }

            .workflow-actions {
              justify-content: center;
              margin-top: 12px;
            }

            .status-badge-card {
              position: static;
              margin: 12px auto;
              display: block;
              width: fit-content;
            }
          }

          @media (max-width: 576px) {
            .workflow-icon {
              width: 60px;
              height: 60px;
            }

            .workflow-title {
              font-size: 16px;
            }

            .workflow-meta-item {
              font-size: 12px;
            }

            .workflow-date {
              font-size: 12px;
            }

            .action-btn-card {
              width: 32px;
              height: 32px;
            }

            .status-badge-card {
              padding: 4px 8px;
              font-size: 11px;
            }

            .modal-dialog {
              margin: 1rem;
            }
          }

          .submitted-data-list {
            padding-left: 0;
          }

          .submitted-data-item {
            margin-bottom: 8px;
          }

          .image-container {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .image-container img {
            cursor: pointer;
            transition: transform 0.2s;
          }

          .image-container img:hover {
            transform: scale(1.05);
          }

          .custom-textarea {
            resize: none;
            background: #f8f9fa;
        `}
      </style>
      <Breadcrumb title="WorkFlow Instances History" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex flex-column flex-md-row justify-content-md-between gap-3 w-100">
            {/* Search Input */}
            <div className="col-12 col-md-6 mb-3 mb-md-0">
              <div className="d-flex align-items-center gap-3 flex-grow-1">
                <div className="navbar-search flex-grow-1">
                  <input
                    type="text"
                    className="form-control h-40-px w-100"
                    name="search"
                    placeholder="Search by Vehicle Name, or Instance ID"
                    value={filters.search}
                    onChange={handleSearchChange}
                    ref={searchInputRef}
                    aria-label="Search workflow instances by name, vehicle, or ID"
                  />
                  <i className="bi bi-search icon"></i>
                </div>
              </div>
            </div>

            {/* Per Page and Status Filter */}
            <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
              <PerPageDropdown
                perPage={pagination.perPage}
                handlePerPageChange={handlePerPageChange}
              />
              <div className="d-flex align-items-center gap-2">
                {/* 👈 NEW: Info Button */}
                <button
                  type="button"
                  className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Information about Workflow Instances History"
                  onClick={handleInfoClick}
                  aria-label="Page Information"
                >
                  <Icon icon="lucide:info" className="menu-icon" />
                </button>
                <div style={{ minWidth: "150px" }}>
                  <CustomSelect
                    name="status"
                    value={filters.status}
                    onChange={handleStatusFilterChange}
                    options={[
                      { label: "Completed", value: "completed" },
                      { label: "Cancelled", value: "cancelled" },
                    ]}
                    placeholder="Select Status"
                    isMulti={false}
                    isClearable={true}
                    isSearchable={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body p-24">
          {loading ? (
            // <div className="shimmer-wrapper">
            //   {Array.from({ length: pagination.perPage }).map((_, index) => (
            //     <div
            //       key={index}
            //       className="shimmer-card"
            //       style={{ background: "var(--bg-color)" }}
            //     ></div>
            //   ))}
            // </div>
            <div className="row gy-4">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="">
                  <ShimmerWorkflowCardFull />
                </div>
              ))}
            </div>
          ) : instances.length === 0 ? (
            <TableDataNoAvailable />
          ) : (
            <div className="workflow-instances-list">
              {instances.map((instance) => (
                <div
                  key={instance.id}
                  className="workflow-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(instance);
                  }}
                >
                  <div className="workflow-card-body">
                    <span
                      className={`status-badge-card status-${instance.status_label}`}
                    >
                      {instance.status}
                    </span>
                    <div className="workflow-card-header">
                      <div className="workflow-icon">
                        {getVehicleImage(instance.vehicle_images)}
                      </div>
                      <div className="workflow-content">
                        <h6
                          className="workflow-title"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(instance);
                          }}
                          style={{ cursor: "pointer" }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewDetails(instance);
                            }
                          }}
                        >
                          {instance.vehicle_name}
                        </h6>
                        <div className="workflow-meta">
                          <div className="workflow-meta-item">
                            <Icon
                              icon="heroicons:building-office"
                              className="icon"
                            />
                            <span>
                              {instance.location_name || "No location"}
                            </span>
                          </div>
                          <div className="workflow-meta-item">
                            <Icon
                              icon="heroicons:identification"
                              className="icon"
                            />
                            <span>{instance.id}</span>
                          </div>
                          {instance.step_count > 0 && (
                            <div className="workflow-meta-item">
                              <Icon
                                icon="heroicons:list-bullet"
                                className="icon"
                              />
                              <span>{instance.step_count} steps</span>
                            </div>
                          )}
                        </div>
                        <div className="workflow-date">
                          <Icon icon="heroicons:clock" className="icon" />
                          <span>{instance.created_at}</span>
                        </div>
                        <div className="workflow-actions">
                          {!instance.is_completed && !instance.is_cancelled && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancel(instance);
                              }}
                              className="action-btn-card action-btn-cancel"
                              title="Cancel workflow"
                            >
                              <Icon
                                icon="heroicons:x-mark"
                                style={{ fontSize: "18px" }}
                              />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(instance);
                            }}
                            className="action-btn-card action-btn-view"
                            title="View Details"
                          >
                            <Icon
                              icon="heroicons:eye"
                              style={{ fontSize: "18px" }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Step History Modal */}
      <StepHistoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedInstance(null);
        }}
        instance={selectedInstance}
        title="Instance History"
      />

      {/* Cancel Confirmation Modal */}
      {showCancelConfirmModal && instanceToCancel && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Cancellation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseCancelModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to cancel the workflow instance{" "}
                  <strong>{instanceToCancel.vehicle_name}</strong> (ID:{" "}
                  {instanceToCancel.id})?
                </p>
                <p>This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseCancelModal}
                >
                  No, Keep Workflow
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmCancel}
                >
                  Yes, Cancel Workflow
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Slideshow Modal */}
      {showImageModal && selectedImages.length > 0 && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <div
            className="modal-dialog"
            style={{
              maxWidth: "800px",
              width: "90%",
              maxHeight: "600px",
              height: "auto",
            }}
          >
            <div className="modal-content image-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Vehicle Images</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseImageModal}
                ></button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={selectedImages[currentImageIndex]}
                  alt={`Vehicle Image ${currentImageIndex + 1}`}
                  className="image-modal-img"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "450px",
                    objectFit: "contain",
                  }}
                />
                <div className="image-controls">
                  <button
                    className="image-control-btn"
                    onClick={handlePrevImage}
                  >
                    <Icon
                      icon="heroicons:chevron-left"
                      style={{ fontSize: "20px" }}
                    />
                  </button>
                  <span className="image-index">
                    {currentImageIndex + 1} / {selectedImages.length}
                  </span>
                  <button
                    className="image-control-btn"
                    onClick={handleNextImage}
                  >
                    <Icon
                      icon="heroicons:chevron-right"
                      style={{ fontSize: "20px" }}
                    />
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseImageModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 👈 NEW: Info Modal - EXACTLY SAME STYLE AS Other Pages */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={handleInfoClose}
        title="Workflow Instances History - Complete Overview"
      >
        <p className="text-neutral-900 mb-4">
          This page is your <strong>complete audit trail</strong> showing every
          workflow instance that has ever run in your system. Think of it as the{" "}
          <strong>historical record</strong> of all customer interactions and
          automated processes.
        </p>

        <h6 className="fw-bold text-primary-600 mb-3">What You See Here</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Vehicle Information</strong> – Customer vehicles that went
            through workflows
          </li>
          <li className="list-group-item">
            <strong>Instance ID</strong> – Unique identifier for each workflow
            run
          </li>
          <li className="list-group-item">
            <strong>Location</strong> – Where the workflow was executed
          </li>
          <li className="list-group-item">
            <strong>Step Count</strong> – How many steps were completed
          </li>
          <li className="list-group-item">
            <strong>Status</strong> – Current state (Completed, Cancelled, In
            Progress)
          </li>
          <li className="list-group-item">
            <strong>Timestamp</strong> – When the workflow started
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">
          Status Types Explained
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Completed (Green)</strong>
            <br />
            • All steps finished successfully
            <br />
            • Customer journey completed
            <br />• Ready for analysis and reporting
          </li>
          <li className="list-group-item">
            <strong>Cancelled (Red)</strong>
            <br />
            • Workflow was stopped manually
            <br />
            • Shows where it was cancelled
            <br />• Useful for troubleshooting issues
          </li>
          <li className="list-group-item">
            <strong>In Progress (Yellow)</strong>
            <br />
            • Currently active workflows
            <br />
            • Being processed right now
            <br />• Can be monitored in real-time
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">Key Actions Available</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>View Details (Eye Icon)</strong>
            <br />
            • See complete step-by-step history
            <br />
            • View all submitted data and images
            <br />• Track exactly what happened when
          </li>
          <li className="list-group-item">
            <strong>Cancel Workflow (X Icon)</strong>
            <br />
            • Stop in-progress workflows
            <br />
            • Only available for active instances
            <br />• Use when something goes wrong
          </li>
          <li className="list-group-item">
            <strong>Vehicle Images</strong>
            <br />
            • Click vehicle icon to see all photos
            <br />
            • Automatic slideshow of all angles
            <br />• Front, back, left, right views
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">
          Search & Filter Capabilities
        </h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Search by Vehicle</strong> – Find specific customer vehicles
          </li>
          <li className="list-group-item">
            <strong>Search by Instance ID</strong> – Locate exact workflow runs
          </li>
          <li className="list-group-item">
            <strong>Filter by Status</strong> – View only completed or cancelled
            workflows
          </li>
          <li className="list-group-item">
            <strong>Pagination</strong> – Navigate through large datasets easily
          </li>
        </ul>

        <div className="alert alert-info small mt-4">
          <strong>Pro Tips:</strong>
          <br />
          • Use "View Details" to investigate customer issues or complaints
          <br />
          • Check cancelled workflows to identify process problems
          <br />
          • Search by vehicle to see complete customer history
          <br />
          • Use status filters for focused analysis
          <br />• The step history shows exact timestamps and data submitted
        </div>

        <p className="text-neutral-900 mt-4 fw-bold text-primary">
          Remember: Complete history = Better customer service + Process
          improvement opportunities!
        </p>
        <p className="text-neutral-900 small">
          Business Impact: Audit trails ensure accountability, historical data
          enables trend analysis, and detailed records help resolve customer
          disputes quickly.
        </p>
      </InfoModal>
    </>
  );
};

export default WorkFlowInstanceHistory;
const ShimmerWorkflowCardFull = () => (
  <div className="placeholder-glow">
    <div
      className="placeholder w-100 h-60-px rounded-12"
      style={{ minHeight: "150px" }}
    ></div>
  </div>
);
